<template>
    <BaseContainer>
        <template slot="menu">
            <v-app-bar app dark :class="classMenu" :style="cssVars">
                <v-btn icon :color="$vuetify.theme.contrast.color" @click="onClickGoBack()">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-spacer />
                <span :style="`text-transform: uppercase; color: ${$vuetify.theme.contrast.color};`">
                    <b>{{ produto && produto.descricao }}</b>
                </span>
                <v-spacer />
            </v-app-bar>
        </template>
        <template slot="principal">
            <v-main>
                <EtapaProgresso :etapas="etapas" :indice="etapaAtual" />

                <v-card v-if="passo" elevation="0" class="et-cab">
                    <div class="et-cab__linha">
                        <div class="et-cab__txt">
                            <!-- Combo: de qual item desse combo é essa escolha? -->
                            <div v-if="contexto" class="et-cab__ctx" :style="{ color: corDoContexto }">{{ contexto }}</div>
                            <div class="et-cab__tit">{{ passo.titulo }}</div>
                            <div class="et-cab__sub">{{ instrucaoDaEtapa(passo) }}</div>
                        </div>
                        <EtapaSelo :passo="passo" />
                    </div>

                    <!-- Só quando existe adicional: "R$ 0,00" não é informação. -->
                    <div v-if="valorAdicional" class="et-cab__adicional">
                        {{ $t('etapa.valorAdicional', { valor: formatMoney(valorAdicional) }) }}
                    </div>

                    <div v-if="categorias.length > 1" class="et-cab__categorias">
                        <v-btn
                            :outlined="!!categoriaFilter"
                            color="primary"
                            class="mr-1"
                            depressed
                            tile
                            small
                            @click="categoriaFilter = ''"
                        >
                            {{ $t('geral.todas') }}
                        </v-btn>
                        <v-btn
                            v-for="categoria in categorias" :key="categoria"
                            :outlined="categoriaFilter !== categoria"
                            color="primary"
                            class="mr-1"
                            min-width="100px"
                            depressed
                            tile
                            small
                            @click="categoriaFilter = categoria"
                        >
                            {{ categoria }}
                        </v-btn>
                    </div>

                    <div v-if="showPesquisa" class="et-cab__busca">
                        <v-text-field
                            v-model="search"
                            dense
                            outlined
                            clearable
                            hide-details
                            :label="$t('geral.pesquisar')"
                            prepend-inner-icon="mdi-magnify"
                        />
                    </div>
                </v-card>

                <div class="et-opcoes">
                    <EtapaOpcao
                        v-for="complemento in complementosFiltrados"
                        :key="complemento.id"
                        :passo="passo"
                        :complemento="complemento"
                        :chip-estoque="getEstoqueText(complemento)"
                        :editavel="qtdMaxDoComplemento(passo, complemento) > 1"
                        @tocar="tocar(passo, $event)"
                        @somar="somar(passo, $event)"
                        @remover="remover($event)"
                        @quantidade="onChangeQuantidade"
                    />

                    <div v-if="!complementosFiltrados.length" class="et-opcoes__vazio">
                        {{ $t('etapa.nenhumaOpcao') }}
                    </div>
                </div>

                <v-footer fixed class="pa-4">
                    <v-btn
                        color="primary"
                        x-large
                        block
                        class="et-cta"
                        :style="{ color: trava ? null : corSobrePrimaria }"
                        :disabled="!!trava"
                        @click="onClickNext"
                    >
                        <span class="et-cta__txt">{{ rotuloDoBotao }}</span>
                        <v-icon v-if="proximoProduto && !trava" right>mdi-arrow-right</v-icon>
                    </v-btn>
                </v-footer>
            </v-main>
        </template>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import EtapaOpcao from '@/components/EtapaOpcao';
import EtapaProgresso from '@/components/EtapaProgresso';
import EtapaSelo from '@/components/EtapaSelo';
import { valorTotalComplementos } from '@/utils/calc';
import { clone, corSobre } from '@/utils/utils';
import {
    definirQuantidade,
    etapasDoProduto,
    instrucaoDaEtapa,
    limparEscolhasVazias,
    pendenciaDaEtapa,
    qtdMaxDoComplemento,
    remover,
    somar,
    tocar,
    zerarEscolhas,
} from '@/utils/etapa';
import { mapMutations } from 'vuex';

export default {
    name: 'Steps',

    components: { BaseContainer, EtapaOpcao, EtapaProgresso, EtapaSelo },

    data: () => ({
        search: '',
        cart: null,
        produto: null,
        etapaAtual: 0,
        categoriaFilter: '',
        finalizando: false,
    }),

    computed: {
        isDark() {
            return this.$vuetify.theme.isDark;
        },

        classMenu() {
            return this.isDark ? 'app-bar-dark' : 'app-bar-light';
        },

        cssVars() {
            return {
                '--bg-color': this.$vuetify.theme.custom.appBarColor,
                'padding': '0 0 10px 0',
            };
        },

        etapas() {
            return etapasDoProduto(this.produto);
        },

        etapa() {
            return this.etapas[this.etapaAtual] || null;
        },

        passo() {
            return this.etapa?.passo || null;
        },

        contexto() {
            return this.etapa?.contexto || '';
        },

        ehUltimaEtapa() {
            return this.etapaAtual >= this.etapas.length - 1;
        },

        proximoProduto() {
            const proxima = this.etapas[this.etapaAtual + 1];

            if (!proxima?.contexto || proxima.contexto === this.contexto) {
                return null;
            }

            return proxima.contexto;
        },

        corDoContexto() {
            const primaria = this.$vuetify.theme.custom?.primaryColor;

            return corSobre(primaria, '#000') === '#fff' ? primaria : null;
        },

        corSobrePrimaria() {
            return corSobre(this.$vuetify.theme.custom?.primaryColor);
        },

        rotuloDoBotao() {
            if (this.trava) {
                return this.trava;
            }

            if (this.proximoProduto) {
                return this.$t('etapa.irPara', { produto: this.proximoProduto });
            }

            return this.ehUltimaEtapa ? this.$t('geral.concluir') : this.$t('geral.continuar');
        },

        trava() {
            return this.passo ? pendenciaDaEtapa(this.passo) : null;
        },

        valorAdicional() {
            return this.passo ? valorTotalComplementos(this.passo) : 0;
        },

        complementos() {
            return this.passo?.complementos || [];
        },

        complementosFiltrados() {
            if (this.search) {
                const termo = this.search.trim().toLowerCase();
                return this.complementos.filter(c => c.descricao.trim().toLowerCase().includes(termo));
            }

            return this.categoriaFilter
                ? this.complementos.filter(c => c.categoria?.descricao === this.categoriaFilter)
                : this.complementos;
        },

        categorias() {
            const uniques = [];

            this.complementos.forEach(c => {
                const descricao = c.categoria?.descricao;
                descricao && !uniques.includes(descricao) && uniques.push(descricao);
            });

            return uniques;
        },

        showPesquisa() {
            return this.complementos.length > 10;
        },
    },

    mounted() {
        this.initialize();
    },

    methods: {
        ...mapMutations({
            setCart: 'setCart',
        }),

        initialize() {
            const produtoAtual = this.$store.getters.getCurrentProduct;

            // Recarregar a página nesta tela (atualização do app, F5) zera a store. Sem o
            // produto, o finalizar() abaixo empurraria um item vazio para o carrinho.
            if (!produtoAtual) {
                this.$router.replace(`/${this.$route.params.emp}`);
                return;
            }

            this.cart = this.$store.getters.getCart;
            this.produto = zerarEscolhas(clone(produtoAtual));

            if (!this.etapas.length) {
                this.finalizar();
            }
        },

        instrucaoDaEtapa(passo) {
            return instrucaoDaEtapa(passo);
        },

        tocar(passo, complemento) {
            tocar(passo, complemento);
        },

        somar(passo, complemento) {
            somar(passo, complemento);
        },

        remover(complemento) {
            remover(complemento);
        },

        qtdMaxDoComplemento(passo, complemento) {
            return qtdMaxDoComplemento(passo, complemento);
        },

        onChangeQuantidade({ complemento, quantidade }) {
            const { aviso } = definirQuantidade(this.passo, complemento, quantidade);

            aviso && this.notify(aviso, 'warning');
        },

        onClickNext() {
            if (this.trava) {
                return;
            }

            this.limparFiltros();

            if (!this.ehUltimaEtapa) {
                this.etapaAtual++;
                this.scrollTop();
                return;
            }

            this.finalizar();
        },

        finalizar() {
            if (this.finalizando) {
                return;
            }

            this.finalizando = true;

            this.cart.push(limparEscolhasVazias(clone(this.produto)));
            this.setCart(this.cart);
            this.$router.replace(`/${this.$route.params.emp}/resumo`);
        },

        onClickGoBack() {
            if (this.etapaAtual === 0) {
                this.$router.push(`/${this.$route.params.emp}`);
                return;
            }

            this.limparFiltros();
            this.etapaAtual--;
            this.scrollTop();
        },

        limparFiltros() {
            this.search = '';
            this.categoriaFilter = '';
        },

        scrollTop() {
            window.scrollTo(0, 0);
        },

        getEstoqueText(complemento) {
            if (!complemento.controle_estoque || complemento.estoque <= 0) {
                return '';
            }

            return complemento.estoque > 1
                ? this.$t('produto.restamUnidades', { n: complemento.estoque })
                : this.$t('produto.restaUma');
        },
    },
};
</script>

<style scoped>
.app-bar-dark {
    background-color: var(--bg-color) !important;
}

.app-bar-light {
    background-color: var(--bg-color) !important;
}

.et-cab {
    padding: 12px 14px 10px;
    background: transparent !important;
}
.et-cab__linha {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.et-cab__txt { flex: 1; min-width: 0; }
.et-cab__ctx {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    opacity: 0.85;
    margin-bottom: 2px;
}
.et-cab__tit { font-size: 20px; font-weight: 800; line-height: 1.2; }
.et-cab__sub { font-size: 13px; color: #6b7178; margin-top: 2px; }
.theme--dark .et-cab__sub { color: #b0b6bd; }
.et-cab__adicional { font-size: 13px; color: #0f8a3d; font-weight: 700; margin-top: 6px; }

.et-cab__categorias {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    padding: 10px 0 2px;
    gap: 2px;
}
.et-cab__busca { margin-top: 10px; }

.et-opcoes {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 9px;
    align-content: start;
    padding: 4px 14px 96px;
}
@media (min-width: 720px) {
    .et-opcoes { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
}
.et-cta {
    height: auto !important;
    min-height: 52px;
    flex-shrink: 1;
    max-width: 100%;
}
.et-cta >>> .v-btn__content {
    flex: 1 1 auto;
    min-width: 0;
    padding: 8px 0;
}
.et-cta__txt {
    min-width: 0;
    white-space: normal;
    line-height: 1.25;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.et-opcoes__vazio {
    font-size: 13.5px;
    color: #8b9098;
    font-style: italic;
    padding: 12px 2px;
}

</style>
